import React from 'react';
import { Package, Image, MessageSquare, Settings, Home, RefreshCw, ImageIcon, LogOut, BarChart3, ShoppingCart } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Dashboard from './admin/Dashboard';
import OrderManagement from './admin/OrderManagement';
import ProductManagement from './admin/ProductManagement';
import CategoryManagement from './admin/CategoryManagement';
import HeroManagement from './admin/HeroManagement';
import TestimonialManagement from './admin/TestimonialManagement';
import StoreSettings from './admin/StoreSettings';
import MediaBank from './admin/MediaBank';
import { useStoreData } from '../hooks/useStoreData';
import { useAuth } from '../contexts/AuthContext';

const AdminDashboard = ({ onBackToStore }) => {
  const { loading, error, refresh } = useStoreData();
  const { signOut, currentUser } = useAuth();

  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      try {
        await signOut();
        onBackToStore();
      } catch (error) {
        console.error('Sign out error:', error);
        alert('Failed to sign out');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4a1a1f] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading data from Firebase...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading data: {error}</p>
          <button
            onClick={refresh}
            className="bg-[#4a1a1f] text-white px-4 py-2 rounded-lg hover:bg-[#5a2a2f]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#4a1a1f] text-white p-6 shadow-lg">
        <div className="container mx-auto">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Admin Dashboard</h1>
              <p className="text-[#c9a86a] mt-1">Manage your Abaya Store with Firebase</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right mr-4">
                <p className="text-sm text-gray-300">Logged in as:</p>
                <p className="text-[#c9a86a] font-medium">{currentUser?.username}</p>
              </div>
              <button
                onClick={refresh}
                className="flex items-center gap-2 bg-[#c9a86a] text-[#4a1a1f] px-4 py-2 rounded-lg hover:bg-white transition-all font-semibold"
              >
                <RefreshCw size={18} />
                Refresh
              </button>
              <button
                onClick={onBackToStore}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-all"
              >
                <Home size={18} />
                Store
              </button>
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 bg-red-600/80 hover:bg-red-600 px-4 py-2 rounded-lg transition-all"
              >
                <LogOut size={18} />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto p-6">
        <Tabs defaultValue="dashboard" className="w-full">
          <TabsList className="grid w-full grid-cols-8 mb-8">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <BarChart3 size={18} />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center gap-2">
              <ShoppingCart size={18} />
              Orders
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center gap-2">
              <Package size={18} />
              Products
            </TabsTrigger>
            <TabsTrigger value="media" className="flex items-center gap-2">
              <ImageIcon size={18} />
              Media Bank
            </TabsTrigger>
            <TabsTrigger value="categories" className="flex items-center gap-2">
              <Settings size={18} />
              Categories
            </TabsTrigger>
            <TabsTrigger value="hero" className="flex items-center gap-2">
              <Image size={18} />
              Hero Banners
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="flex items-center gap-2">
              <MessageSquare size={18} />
              Testimonials
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings size={18} />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <Dashboard />
          </TabsContent>

          <TabsContent value="orders">
            <OrderManagement />
          </TabsContent>

          <TabsContent value="products">
            <ProductManagement />
          </TabsContent>

          <TabsContent value="media">
            <MediaBank />
          </TabsContent>

          <TabsContent value="categories">
            <CategoryManagement />
          </TabsContent>

          <TabsContent value="hero">
            <HeroManagement />
          </TabsContent>

          <TabsContent value="testimonials">
            <TestimonialManagement />
          </TabsContent>

          <TabsContent value="settings">
            <StoreSettings />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
